import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export type ArchPage = {
  id: string;
  parent_id: string | null;
  title: string;
  icon: string | null;
  updated_at: string;
};

type Layout = {
  id: string;
  title: string;
  icon: string | null;
  childCount: number;
  x: number;
  y: number;
  w: number;
  h: number;
  inX: number;
  inY: number;
  outX: number;
  outY: number;
};

type Edge = { from: string; to: string };
type View = { panX: number; panY: number; scale: number };

const NODE_W = 220;
const NODE_H = 86;
const GAP_X = 72;
const GAP_Y = 28;
const MIN_SCALE = 0.25;
const MAX_SCALE = 2.5;

function layoutPages(pages: ArchPage[]): { layouts: Layout[]; edges: Edge[] } {
  const byId = new Map(pages.map((p) => [p.id, p]));
  const kids = new Map<string | null, ArchPage[]>();
  for (const p of pages) {
    const parent = p.parent_id && byId.has(p.parent_id) ? p.parent_id : null;
    if (!kids.has(parent)) kids.set(parent, []);
    kids.get(parent)!.push(p);
  }
  for (const arr of kids.values()) {
    arr.sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" }));
  }

  const layouts: Layout[] = [];
  const edges: Edge[] = [];
  let cursorY = 40;

  function place(node: ArchPage, depth: number, y: number): number {
    const children = kids.get(node.id) || [];
    const x = 40 + depth * (NODE_W + GAP_X);
    let height = NODE_H;
    let childY = y;
    if (children.length) {
      const starts: number[] = [];
      for (const child of children) {
        starts.push(childY);
        const used = place(child, depth + 1, childY);
        edges.push({ from: node.id, to: child.id });
        childY += used + GAP_Y;
      }
      const first = starts[0];
      const last = starts[starts.length - 1];
      const mid = (first + last) / 2;
      height = Math.max(NODE_H, childY - y - GAP_Y);
      layouts.push({
        id: node.id,
        title: node.title || "Untitled",
        icon: node.icon,
        childCount: children.length,
        x,
        y: mid,
        w: NODE_W,
        h: NODE_H,
        inX: x,
        inY: mid + NODE_H / 2,
        outX: x + NODE_W,
        outY: mid + NODE_H / 2,
      });
      return height;
    }
    layouts.push({
      id: node.id,
      title: node.title || "Untitled",
      icon: node.icon,
      childCount: 0,
      x,
      y,
      w: NODE_W,
      h: NODE_H,
      inX: x,
      inY: y + NODE_H / 2,
      outX: x + NODE_W,
      outY: y + NODE_H / 2,
    });
    return height;
  }

  const roots = kids.get(null) || [];
  for (const root of roots) {
    const used = place(root, 0, cursorY);
    cursorY += used + GAP_Y + 12;
  }

  return { layouts, edges };
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function fit(ctx: CanvasRenderingContext2D, valueRaw: string, max: number): string {
  const value = String(valueRaw || "").replace(/\s+/g, " ").trim();
  if (ctx.measureText(value).width <= max) return value;
  let low = 0;
  let high = value.length;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (ctx.measureText(`${value.slice(0, mid)}…`).width <= max) low = mid;
    else high = mid - 1;
  }
  return `${value.slice(0, low)}…`;
}

function wire(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  color: string,
  width: number,
) {
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  const mx = (x1 + x2) / 2;
  ctx.bezierCurveTo(mx, y1, mx, y2, x2, y2);
  ctx.stroke();
  const angle = Math.atan2(y2 - y1, Math.max(24, x2 - x1));
  const size = 4 + width * 1.6;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - size * Math.cos(angle - 0.45), y2 - size * Math.sin(angle - 0.45));
  ctx.lineTo(x2 - size * Math.cos(angle + 0.45), y2 - size * Math.sin(angle + 0.45));
  ctx.closePath();
  ctx.fill();
}

type Props = {
  pages: ArchPage[];
  emptyLabel: string;
  pageKindLabel: string;
  insideLabel: string;
};

export function HeeArchGraph({ pages, emptyLabel, pageKindLabel, insideLabel }: Props) {
  const nav = useNavigate();
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const viewRef = useRef<View>({ panX: 24, panY: 24, scale: 1 });
  const selectedRef = useRef<string | null>(null);
  const dragRef = useRef<{
    pointerId: number;
    sx: number;
    sy: number;
    panX: number;
    panY: number;
    moved: boolean;
    hitId: string | null;
  } | null>(null);
  const drawRef = useRef<() => void>(() => undefined);
  const graphRef = useRef({ layouts: [] as Layout[], edges: [] as Edge[] });
  const labelsRef = useRef({ pageKindLabel, insideLabel });

  const [selected, setSelected] = useState<string | null>(null);
  const [zoomPct, setZoomPct] = useState(100);
  const [panning, setPanning] = useState(false);

  const graph = useMemo(() => layoutPages(pages), [pages]);
  const layoutMap = useMemo(() => new Map(graph.layouts.map((row) => [row.id, row])), [graph]);

  graphRef.current = graph;
  labelsRef.current = { pageKindLabel, insideLabel };
  selectedRef.current = selected;

  useEffect(() => {
    const stage = stageRef.current;
    const canvas = canvasRef.current;
    if (!stage || !canvas) return;

    function draw() {
      const st = stageRef.current;
      const cv = canvasRef.current;
      if (!st || !cv) return;
      const width = st.clientWidth;
      const height = st.clientHeight;
      if (!width || !height) return;
      const dpr = window.devicePixelRatio || 1;
      const pxW = Math.round(width * dpr);
      const pxH = Math.round(height * dpr);
      if (cv.width !== pxW || cv.height !== pxH) {
        cv.width = pxW;
        cv.height = pxH;
      }
      const ctx = cv.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const view = viewRef.current;
      const { layouts, edges } = graphRef.current;
      const map = new Map(layouts.map((row) => [row.id, row]));
      const labels = labelsRef.current;

      ctx.fillStyle = "#16161a";
      ctx.fillRect(0, 0, width, height);
      const step = 20 * view.scale;
      if (step >= 6) {
        const ox = ((view.panX % step) + step) % step;
        const oy = ((view.panY % step) + step) % step;
        const dot = Math.max(1, view.scale * 1.1);
        ctx.fillStyle = "#2a2a32";
        for (let x = ox; x < width; x += step) {
          for (let y = oy; y < height; y += step) ctx.fillRect(x - dot / 2, y - dot / 2, dot, dot);
        }
      }
      ctx.save();
      ctx.translate(view.panX, view.panY);
      ctx.scale(view.scale, view.scale);
      const inv = 1 / view.scale;

      for (const edge of edges) {
        const from = map.get(edge.from);
        const to = map.get(edge.to);
        if (!from || !to) continue;
        wire(ctx, from.outX + 5, from.outY, to.inX - 5, to.inY, "#578cff", 2.1 * Math.max(inv, 0.55));
      }

      for (const node of layouts) {
        const on = node.id === selectedRef.current;
        if (on) {
          ctx.strokeStyle = "#3ec6ff";
          ctx.lineWidth = 2 * inv;
          roundRect(ctx, node.x - 2, node.y - 2, node.w + 4, node.h + 4, 13);
          ctx.stroke();
        }
        ctx.fillStyle = "#242428";
        roundRect(ctx, node.x, node.y, node.w, node.h, 12);
        ctx.fill();
        ctx.fillStyle = "#1c1c20";
        roundRect(ctx, node.x + 10, node.y + 10, node.w - 20, node.h - 20, 8);
        ctx.fill();

        ctx.fillStyle = "#8a8a98";
        ctx.font = "11px Segoe UI, sans-serif";
        ctx.fillText(labels.pageKindLabel, node.x, node.y - 8);

        ctx.fillStyle = "#4ade80";
        ctx.beginPath();
        ctx.arc(node.x + 24, node.y + 28, 3.5, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = "#ffffff";
        ctx.font = "600 13px Segoe UI, sans-serif";
        const label = node.icon ? `${node.icon} ${node.title}` : node.title;
        ctx.fillText(fit(ctx, label, node.w - 56), node.x + 34, node.y + 32);

        ctx.fillStyle = "#9a9aa8";
        ctx.font = "11px Consolas, monospace";
        ctx.fillText(fit(ctx, node.id.slice(0, 8), node.w - 44), node.x + 20, node.y + 52);

        const sub = node.childCount ? `${node.childCount} ${labels.insideLabel}` : "";
        if (sub) {
          ctx.fillStyle = "#dcb67a";
          ctx.font = "11px Segoe UI, sans-serif";
          ctx.fillText(sub, node.x + 20, node.y + 68);
        }

        ctx.fillStyle = "#578cff";
        ctx.beginPath();
        ctx.arc(node.inX, node.inY, 4.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(node.outX, node.outY, 4.5, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }

    drawRef.current = draw;
    draw();
    const ro = new ResizeObserver(() => draw());
    ro.observe(stage);

    function onWheel(e: WheelEvent) {
      e.preventDefault();
      e.stopPropagation();
      const cv = canvasRef.current;
      if (!cv) return;
      const view = viewRef.current;
      const rect = cv.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, view.scale * factor));
      const before = 1 / view.scale;
      const after = 1 / next;
      viewRef.current = {
        scale: next,
        panX: mx - (mx - view.panX) * (after / before),
        panY: my - (my - view.panY) * (after / before),
      };
      setZoomPct(Math.round(next * 100));
      drawRef.current();
    }

    canvas.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      ro.disconnect();
      canvas.removeEventListener("wheel", onWheel);
    };
  }, [graph, pageKindLabel, insideLabel]);

  useEffect(() => {
    drawRef.current();
  }, [selected]);

  function worldOf(clientX: number, clientY: number) {
    const rect = canvasRef.current!.getBoundingClientRect();
    const view = viewRef.current;
    return {
      x: (clientX - rect.left - view.panX) / view.scale,
      y: (clientY - rect.top - view.panY) / view.scale,
    };
  }

  function hit(x: number, y: number): Layout | null {
    const layouts = graphRef.current.layouts;
    for (let i = layouts.length - 1; i >= 0; i -= 1) {
      const n = layouts[i];
      if (x >= n.x && x <= n.x + n.w && y >= n.y && y <= n.y + n.h) return n;
    }
    return null;
  }

  function setScaleAroundCenter(next: number) {
    const st = stageRef.current;
    if (!st) return;
    const view = viewRef.current;
    const mx = st.clientWidth / 2;
    const my = st.clientHeight / 2;
    const clamped = Math.min(MAX_SCALE, Math.max(MIN_SCALE, next));
    const before = 1 / view.scale;
    const after = 1 / clamped;
    viewRef.current = {
      scale: clamped,
      panX: mx - (mx - view.panX) * (after / before),
      panY: my - (my - view.panY) * (after / before),
    };
    setZoomPct(Math.round(clamped * 100));
    drawRef.current();
  }

  function resetView() {
    viewRef.current = { panX: 24, panY: 24, scale: 1 };
    setZoomPct(100);
    drawRef.current();
  }

  if (!pages.length) {
    return <p className="muted heearch-empty">{emptyLabel}</p>;
  }

  return (
    <div className="heearch">
      <div className="heearch-bar">
        <strong>HeeArch</strong>
        <span className="muted">{pages.length}</span>
        <div className="heearch-zoom-group">
          <button type="button" className="heearch-zoom" aria-label="−" onClick={() => setScaleAroundCenter(viewRef.current.scale / 1.2)}>
            −
          </button>
          <button type="button" className="heearch-zoom heearch-zoom-pct" onClick={resetView} title="Reset">
            {zoomPct}%
          </button>
          <button type="button" className="heearch-zoom" aria-label="+" onClick={() => setScaleAroundCenter(viewRef.current.scale * 1.2)}>
            +
          </button>
        </div>
      </div>
      <div className="heearch-stage" ref={stageRef}>
        <canvas
          ref={canvasRef}
          tabIndex={0}
          className={`heearch-canvas${panning ? " is-panning" : ""}`}
          onPointerDown={(e) => {
            if (e.button !== 0 && e.button !== 1) return;
            e.currentTarget.focus();
            const w = worldOf(e.clientX, e.clientY);
            const node = hit(w.x, w.y);
            dragRef.current = {
              pointerId: e.pointerId,
              sx: e.clientX,
              sy: e.clientY,
              panX: viewRef.current.panX,
              panY: viewRef.current.panY,
              moved: false,
              hitId: node?.id || null,
            };
            try {
              e.currentTarget.setPointerCapture(e.pointerId);
            } catch {
              /* ignore */
            }
          }}
          onPointerMove={(e) => {
            const drag = dragRef.current;
            if (!drag || drag.pointerId !== e.pointerId) return;
            const dx = e.clientX - drag.sx;
            const dy = e.clientY - drag.sy;
            if (!drag.moved && dx * dx + dy * dy < 16) return;
            if (!drag.moved) {
              drag.moved = true;
              setPanning(true);
            }
            viewRef.current = {
              ...viewRef.current,
              panX: drag.panX + dx,
              panY: drag.panY + dy,
            };
            drawRef.current();
          }}
          onPointerUp={(e) => {
            const drag = dragRef.current;
            if (drag?.pointerId === e.pointerId) {
              if (!drag.moved && drag.hitId) setSelected(drag.hitId);
              else if (!drag.moved) setSelected(null);
              dragRef.current = null;
              setPanning(false);
            }
            try {
              e.currentTarget.releasePointerCapture(e.pointerId);
            } catch {
              /* ignore */
            }
          }}
          onPointerCancel={() => {
            dragRef.current = null;
            setPanning(false);
          }}
          onDoubleClick={(e) => {
            const w = worldOf(e.clientX, e.clientY);
            const node = hit(w.x, w.y);
            if (node) nav(`/p/${node.id}`);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && selected) nav(`/p/${selected}`);
            if (e.key === "+" || e.key === "=") setScaleAroundCenter(viewRef.current.scale * 1.2);
            if (e.key === "-" || e.key === "_") setScaleAroundCenter(viewRef.current.scale / 1.2);
            if (e.key === "0") resetView();
          }}
        />
      </div>
      {selected ? (
        <div className="heearch-hint">
          <button type="button" className="btn" onClick={() => nav(`/p/${selected}`)}>
            {layoutMap.get(selected)?.title || "Open"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
